import { Form } from '../components';
import DateTimePicker from '../components/DateTimePicker';
import productUtils from '../utils/product';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { getTimezoneFromCountry } from '../utils';

const findFirstAvailableTime = (date, disabledTimeFn) => {
  for (let h = 0; h <= 23; h++) {
    const testDate = dayjs(date).hour(h).minute(0).second(0);
    if (!disabledTimeFn(testDate).disabledHours().includes(h)) {
      for (let m = 0; m <= 59; m++) {
        const testDateAtMin = dayjs(date).hour(h).minute(m).second(0);
        if (!disabledTimeFn(testDateAtMin).disabledMinutes().includes(m)) {
          return dayjs().hour(h).minute(m).second(0);
        }
      }
    }
  }
  return dayjs().startOf('day');
};

const findLastAvailableTime = (date, disabledTimeFn) => {
  for (let h = 23; h >= 0; h--) {
    const testDate = dayjs(date).hour(h).minute(59).second(0);
    if (!disabledTimeFn(testDate).disabledHours().includes(h)) {
      for (let m = 59; m >= 0; m--) {
        const testDateAtMin = dayjs(date).hour(h).minute(m).second(0);
        if (!disabledTimeFn(testDateAtMin).disabledMinutes().includes(m)) {
          return dayjs().hour(h).minute(m).second(0);
        }
      }
    }
  }
  return dayjs().hour(23).minute(59).second(0);
};

const isOutsideSellingDates = (date, product) => {
  const { startSellingDate, endSellingDate } = product;

  if (startSellingDate && date.isBefore(dayjs(startSellingDate))) {
    return true;
  }
  return (endSellingDate && date.isAfter(dayjs(endSellingDate)));
};

const getDisabledStartTime = (date, product) => {
  const timesFromSellingSchedule = productUtils.sellingScheduleDisabledTimes(date, product);
  // For current day we need to disable hours and minutes in the past
  return getScheduleDisabledTimesAddedWithCurrentDayPastTime(dayjs(), date, timesFromSellingSchedule);
};

const getDisabledEndTime = (date, product, startDate, kiosk) => {
  const timesFromSellingSchedule = productUtils.sellingScheduleDisabledTimes(date, product, startDate, kiosk, true);
  // For current day we need to disable hours and minutes in the past
  return getScheduleDisabledTimesAddedWithCurrentDayPastTime(dayjs(startDate), date, timesFromSellingSchedule);
};

const getScheduleDisabledTimesAddedWithCurrentDayPastTime = (now, date, timesFromSellingSchedule) => {
  if (now.isSame(date, 'day')) {
    const hoursInThePast = Array.from({ length: now.hour() }, (value, index) => index);
    const minutesInThePast = now.isSame(date, 'hour') ?
      Array.from({ length: now.minute() < 59 ? now.minute() + 1 : now.minute() }, (value, index) => index) : [];
    return {
      disabledHours: () => [ ...hoursInThePast, ...timesFromSellingSchedule.disabledHours() ],
      disabledMinutes: () => [ ...minutesInThePast, ...timesFromSellingSchedule.disabledMinutes() ],
    };
  } else {
    return timesFromSellingSchedule;
  }
};

/**
 * @param {string} countryCode
 * @param {Object} product
 * @param {dayjs} initialDateTime
 * @param {boolean} hidden
 * @param {Object} kiosk
 * @param {boolean} hideTime
 * @return {JSX.Element}
 */
const ValidityForm = ({ countryCode, product, initialDateTime, hidden, kiosk }) => {
  const { t } = useTranslation();
  const [ startDate, setStartDate ] = useState(initialDateTime);

  const validateDateTime = async (field, value) => {
    if (hidden) {
      return;
    }
    if (!value) {
      throw new Error('value missing');
    }

    // Tolerate 10 minute difference compared to current time
    const dateTime = dayjs(value);
    if (dateTime.isBefore(dayjs().subtract(10, 'minutes')) || dateTime.isBefore(startDate)) {
      throw new Error('before now or start');
    }
    if (!productUtils.isTimeSelectable(value, product, kiosk, getTimezoneFromCountry(countryCode))) {
      throw new Error('invalid time');
    }
  };

  const getStartDefaultTime = date => findFirstAvailableTime(date, d => getDisabledStartTime(d, product));
  const getEndDefaultTime = date => findLastAvailableTime(date, d => getDisabledEndTime(d, product, startDate, kiosk));

  return (<>
    <Form.Item name="validFrom" rules={[ { validator: validateDateTime } ]} help={<div/>} hidden={hidden}>
      <DateTimePicker
        onChange={setStartDate}
        labels={[ t('startDate'), t('startTime') ]}
        countryCode={countryCode}
        disabledDate={date =>
          date.isBefore(dayjs(), 'day') ||
          isOutsideSellingDates(date, product) ||
          productUtils.isOverSelectableEndLimit(dayjs(), date, product, kiosk) ||
          productUtils.isSellingScheduleDisabledStartDate(date, product) ||
          productUtils.isSellingScheduleDisabledCurrentDay(date, product)
        }
        disabledTime={date => getDisabledStartTime(date, product)}
        hideTime={kiosk.hideTimeSelection}
        getDefaultTime={getStartDefaultTime}
      />
    </Form.Item>

    <Form.Item name="validTo" rules={[ { validator: validateDateTime } ]} help={<div/>} hidden={hidden}>
      <DateTimePicker
        labels={[ t('endDate'), t('endTime') ]}
        countryCode={countryCode}
        disabledDate={val =>
          val.isBefore(dayjs(), 'day') ||
          val.isBefore(startDate, 'day') ||
          isOutsideSellingDates(val, product) ||
          productUtils.isOverSelectableEndLimit(dayjs(), val, product, kiosk) ||
          productUtils.isSellingScheduleDisabledEndDate(val, startDate, product)}
        disabledTime={date => getDisabledEndTime(date, product, startDate, kiosk)}
        hideTime={kiosk.hideTimeSelection}
        getDefaultTime={getEndDefaultTime}
      />
    </Form.Item>
  </>);
};

export default ValidityForm;
